import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { STATUS_META, isLimitedMemberView, memberTypeLabel, ROLE_LABELS, MEMBER_TYPE_LABELS } from "@/lib/constants";

const RoleBadge = ({ role, testId }) => {
  const [open, setOpen] = useState(false);
  if (!role || role === "member" || !ROLE_LABELS[role]) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} aria-label={ROLE_LABELS[role]}
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          className="inline-flex align-middle ml-1.5 text-emerald-600 hover:scale-110 transition-transform">
          <Star size={15} weight="fill" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-auto px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-popup`}>
        <p className="font-semibold text-emerald-700 dark:text-emerald-400">{ROLE_LABELS[role]}</p>
        <p className="text-muted-foreground mt-0.5">Unassign the role before archiving.</p>
      </PopoverContent>
    </Popover>
  );
};
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { MagnifyingGlass, CheckCircle, Key, PencilSimple, Printer, DownloadSimple, CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight, CaretUp, CaretDown, CaretUpDown, Archive, Buildings, ArrowClockwise, Coins, ShieldCheck, Star } from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const SortHead = ({ label, col, sortBy, sortDir, onSort, className = "" }) => (
  <TableHead className={className}>
    <button type="button" onClick={() => onSort(col)} data-testid={`sort-${col}`}
      className="inline-flex items-center gap-1 hover:text-foreground transition-colors font-medium">
      {label}
      {sortBy === col
        ? (sortDir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)
        : <CaretUpDown size={12} className="opacity-40" />}
    </button>
  </TableHead>
);
import { RejectDialog } from "@/components/RejectDialog";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { ImportMembersDialog } from "@/components/ImportMembersDialog";
import { AddMemberDialog } from "@/components/AddMemberDialog";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { MemberDetailsDialog } from "@/components/MemberDetailsDialog";
import { isNationalLevel, canApproveRegional as canApproveRegionalFn, ARCHIVE_REASONS, CHAPTERS } from "@/lib/constants";
import { toast } from "sonner";
import { ExportColumnsDialog } from "@/components/ExportColumnsDialog";

const STATUS_OPTIONS = [
  ["all", "All statuses"],
  ["incomplete", "Incomplete"],
  ["pending_facility", "Pending Facility"],
  ["pending_regional", "Pending Regional"],
  ["pending_national", "Pending National"],
  ["deceased", "Deceased"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
  ["dues_paying", "Dues paying"],
  ["non_dues_paying", "Non-dues paying"],
];

const photoUrl = (p) =>
  p ? (p.startsWith("data:") || p.startsWith("http") ? p : `${API}/files/${p}?auth=${localStorage.getItem("token")}`) : "";

export function MemberAvatar({ member, size = 34, testIdPrefix = "member-avatar" }) {
  const names = (member.full_name || `${member.first_name || ""} ${member.last_name || ""}`).trim().split(/\s+/);
  const initials = `${names[0]?.[0] || ""}${names.length > 1 ? names[names.length - 1]?.[0] || "" : ""}`.toUpperCase();
  const src = photoUrl(member.profile_picture);
  return (
    <div
      data-testid={`${testIdPrefix}-${member.staff_id || member.id}`}
      className="rounded-full bg-muted overflow-hidden border border-border flex items-center justify-center shrink-0 text-xs font-semibold text-muted-foreground"
      style={{ width: size, height: size }}
    >
      {src ? <img src={src} alt={member.full_name || "Member photo"} className="h-full w-full object-cover" /> : (initials || "?")}
    </div>
  );
}


export default function Members() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const limitedView = isLimitedMemberView(user.role);
  const [detailLoading, setDetailLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [orgs, setOrgs] = useState({ regions: [], chapters: [], facilities: [] });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [regionFilter, setRegionFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [facilityFilter, setFacilityFilter] = useState("");
  const [facilityPrefix, setFacilityPrefix] = useState("");
  const [prefixLabel, setPrefixLabel] = useState("");
  const [filterOpts, setFilterOpts] = useState({ regions: [], facilities: [], districts: [] });
  const [pageInput, setPageInput] = useState("");
  const [checked, setChecked] = useState({});
  const checkedIds = Object.keys(checked);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiving, setArchiving] = useState(false);
  const showFilters = ["national_manager", "super_admin", "regional_manager"].includes(user.role);
  const canArchive = ["national_manager", "super_admin"].includes(user.role);

  useEffect(() => {
    if (!selected?.id) return;
    let active = true;
    setDetailLoading(true);
    api.get(`/members/${selected.id}`).then(({ data }) => {
      if (active) setSelected(data);
    }).catch((err) => {
      if (active) { toast.error(formatApiError(err.response?.data?.detail)); setSelected(null); }
    }).finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [selected?.id]);

  const orgName = (type, id) => (orgs[type].find((o) => o.id === id)?.name) || "—";
  const facility = (id) => orgs.facilities.find((f) => f.id === id) || {};

  const [searchParams, setSearchParams] = useSearchParams();
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/members", { params: {
        search, status, page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir,
        region_name: regionFilter || undefined, district: districtFilter || undefined,
        facility_name: facilityFilter || undefined, facility_prefix: facilityPrefix || undefined,
        member_type: typeFilter === "all" ? undefined : typeFilter,
      } });
      setMembers(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } finally {
      setLoading(false);
    }
  }, [search, status, page, pageSize, sortBy, sortDir, regionFilter, districtFilter, facilityFilter, facilityPrefix, typeFilter]);

  const ptr = usePullToRefresh(load);

  useEffect(() => { api.get("/orgs").then((r) => setOrgs(r.data)); }, []);
  // Apply filters passed via URL (from the Organization tab links)
  useEffect(() => {
    const r = searchParams.get("region"); const d = searchParams.get("district");
    const f = searchParams.get("facility"); const p = searchParams.get("prefix");
    const cl = searchParams.get("chapter");
    if (r || d || f || p) {
      setRegionFilter(r || ""); setDistrictFilter(d || ""); setFacilityFilter(f || "");
      setFacilityPrefix(p || ""); setPrefixLabel(cl || (p ? `${p}…` : ""));
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    if (showFilters) api.get("/members/filter-options", { params: { region_name: regionFilter || undefined } })
      .then((r) => setFilterOpts(r.data)).catch(() => {});
  }, [showFilters, regionFilter]);
  useEffect(() => { setPage(1); }, [search, status, pageSize, regionFilter, districtFilter, facilityFilter, facilityPrefix, typeFilter]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("asc"); }
  };

  const jumpToPage = () => {
    const n = parseInt(pageInput, 10);
    if (!isNaN(n) && n >= 1 && n <= totalPages) setPage(n);
    setPageInput("");
  };

  const selectedHolders = Object.values(checked).filter((m) => m.role && m.role !== "member");
  const allVisibleChecked = members.length > 0 && members.every((m) => checked[m.id]);
  const toggleAll = () => setChecked((prev) => {
    const next = { ...prev };
    members.forEach((m) => { if (allVisibleChecked) delete next[m.id]; else next[m.id] = m; });
    return next;
  });
  const toggleOne = (m) => setChecked((prev) => {
    const next = { ...prev };
    if (next[m.id]) delete next[m.id]; else next[m.id] = m;
    return next;
  });

  const archiveSelected = async () => {
    setArchiving(true);
    try {
      const { data } = await api.post("/members/archive", { ids: checkedIds, reason: archiveReason.trim() });
      toast.success(`${data.archived} member${data.archived !== 1 ? "s" : ""} archived`);
      setChecked({});
      setArchiveOpen(false);
      setArchiveReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setArchiving(false);
    }
  };

  const handleExport = async (pick) => {
    setExporting(true);
    try {
      const res = await api.get("/export/members", { responseType: "blob", params: {
        ...pick, ids: checkedIds.length ? checkedIds.join(",") : undefined,
        search, status, member_type: typeFilter === "all" ? undefined : typeFilter, region_name: regionFilter || undefined, district: districtFilter || undefined,
        facility_name: facilityFilter || undefined, facility_prefix: facilityPrefix || undefined,
      } });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `members_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Export downloaded");
      setPickOpen(false);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const canApproveRegional = canApproveRegionalFn(user.role);
  const canApproveNational = isNationalLevel(user.role);
  const canReject = canApproveRegionalFn(user.role);
  const canAddMember = ["facility_manager", "chapter_manager", "national_manager", "super_admin"].includes(user.role);

  const resetPw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reset-password/${selected.id}`);
      toast.success(`Temporary password for ${data.staff_id}: ${data.temporary_password}`, {
        description: "Share it with the member — they must change it at next login.", duration: 15000,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const canResetPw = ["national_manager", "super_admin"].includes(user.role);
  const [duesOpen, setDuesOpen] = useState(false);
  const [duesText, setDuesText] = useState("");
  const [savingDues, setSavingDues] = useState(false);
  const openDues = () => {
    api.get("/dues/settings").then((r) => { setDuesText(r.data.how_to_pay || ""); setDuesOpen(true); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  };
  const saveDues = async () => {
    setSavingDues(true);
    try { await api.post("/dues/settings", { how_to_pay: duesText }); toast.success("Dues reminder saved"); setDuesOpen(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingDues(false); }
  };
  const [logOpen, setLogOpen] = useState(false);
  const [logItems, setLogItems] = useState([]);
  const openLog = () => {
    api.get("/staff-id-access-log", { params: { limit: 200 } })
      .then((r) => { setLogItems(r.data.items || []); setLogOpen(true); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  };
  const act = async (path, okMsg, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/members/${selected.id}/${path}`, body || {});
      setSelected(data);
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="animate-fade-up" {...ptr.handlers}>
        {(ptr.pull > 0 || ptr.refreshing) && (
          <div className="md:hidden flex items-center justify-center text-muted-foreground overflow-hidden transition-[height]"
            style={{ height: ptr.refreshing ? 32 : ptr.pull }} data-testid="members-ptr">
            <ArrowClockwise size={20} weight="bold" className={ptr.refreshing ? "animate-spin" : ""}
              style={{ transform: ptr.refreshing ? "none" : `rotate(${ptr.pull * 3}deg)` }} />
          </div>
        )}
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Directory</span>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Members</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {total.toLocaleString()} member{total !== 1 ? "s" : ""} in your scope.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {canAddMember && <AddMemberDialog user={user} facilities={orgs.facilities} onAdded={load} />}
            {isNationalLevel(user.role) && <ImportMembersDialog onImported={load} />}
            {isNationalLevel(user.role) && (
              <Button onClick={openDues} variant="outline" data-testid="dues-settings-button" className="gap-2">
                <Coins size={18} weight="bold" /> Dues reminder
              </Button>
            )}
            {isNationalLevel(user.role) && (
              <Button onClick={openLog} variant="outline" data-testid="staff-id-log-button" className="gap-2">
                <ShieldCheck size={18} weight="bold" /> Staff ID log
              </Button>
            )}
            {isNationalLevel(user.role) && (
              <Button onClick={() => setPickOpen(true)} disabled={exporting} variant="outline"
                data-testid="export-excel-button" className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                <DownloadSimple size={18} weight="bold" />
                {exporting ? "Exporting…" : "Export to Excel"}
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="member-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={limitedView ? "Search name, facility or professional grade…" : "Search name, staff ID, membership ID, phone…"} className="pl-10" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full sm:w-52" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.filter(([v]) => !limitedView || !["dues_paying", "non_dues_paying"].includes(v)).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-52" data-testid="member-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="member-type-filter-all">All membership types</SelectItem>
              {Object.entries(MEMBER_TYPE_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v} data-testid={`member-type-filter-${v}`}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showFilters && (
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-3">
            <SearchableSelect testId="region-filter" className="w-full sm:w-56" placeholder="All regions" allLabel="All regions"
              value={regionFilter} options={filterOpts.regions}
              onChange={(v) => { setRegionFilter(v); setDistrictFilter(""); setFacilityFilter(""); }} />
            <SearchableSelect testId="district-filter" className="w-full sm:w-56" placeholder="All districts" allLabel="All districts"
              value={districtFilter} options={filterOpts.districts} disabled={!regionFilter}
              onChange={setDistrictFilter} />
            <SearchableSelect testId="facility-filter" className="w-full sm:w-64" placeholder="All facilities" allLabel="All facilities"
              value={facilityFilter} options={filterOpts.facilities} disabled={!regionFilter}
              onChange={setFacilityFilter} />
            <SearchableSelect testId="chapter-filter" className="w-full sm:w-52" placeholder="All chapters" allLabel="All chapters"
              value={CHAPTERS.find((c) => c.prefix === facilityPrefix)?.code || ""}
              options={CHAPTERS.map((c) => c.code)}
              onChange={(code) => {
                const ch = CHAPTERS.find((c) => c.code === code);
                setFacilityPrefix(ch ? ch.prefix : "");
                setPrefixLabel(ch ? ch.code : "");
              }} />
            {(regionFilter || districtFilter || facilityFilter) && (
              <Button variant="ghost" size="sm" data-testid="clear-filters"
                onClick={() => { setRegionFilter(""); setDistrictFilter(""); setFacilityFilter(""); }}>Clear filters</Button>
            )}
          </div>
        )}

        {/* Scope headcount summary */}
        {(facilityFilter || regionFilter || districtFilter || facilityPrefix) && (
          <Card className="mt-3 p-4 border-primary/30 bg-primary/5 flex items-center gap-3" data-testid="facility-summary-card">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 shrink-0">
              <Buildings size={22} weight="bold" className="text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">{facilityFilter || districtFilter || regionFilter || prefixLabel || "Chapter"}</p>
              <p className="font-display text-2xl font-bold tabular-nums leading-tight" data-testid="facility-summary-count">
                {total.toLocaleString()} member{total !== 1 ? "s" : ""}
              </p>
            </div>
            {facilityPrefix && (
              <Button variant="ghost" size="sm" data-testid="clear-chapter"
                onClick={() => { setFacilityPrefix(""); setPrefixLabel(""); }}>Clear</Button>
            )}
          </Card>
        )}

        {/* Archive action bar (National Managers only) */}
        {canArchive && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button variant="destructive" disabled={checkedIds.length === 0 || selectedHolders.length > 0}
              onClick={() => setArchiveOpen(true)} data-testid="archive-selected-button" className="gap-2">
              <Archive size={18} weight="bold" /> Archive Selected Members
            </Button>
            {checkedIds.length > 0 && (
              <>
                <span className="text-sm text-muted-foreground" data-testid="archive-selected-count">{checkedIds.length} selected across all pages · Export to Excel will include only these</span>
                <Button variant="ghost" size="sm" onClick={() => setChecked({})} data-testid="clear-selection-button">Clear selection</Button>
              </>
            )}
            {selectedHolders.length > 0 && (
              <span className="text-sm text-amber-700 dark:text-amber-400" data-testid="archive-role-holder-warning">
                Can't archive: {selectedHolders.map((m) => [m.first_name, m.last_name].filter(Boolean).join(" ")).join(", ")} {selectedHolders.length === 1 ? "holds a role" : "hold roles"}. Unassign first.
              </span>
            )}
          </div>
        )}

        {/* Mobile stacked cards */}
        <div className="mt-4 space-y-3 md:hidden" data-testid="members-mobile-list">
          {loading ? (
            <Card className="p-6 text-center text-muted-foreground">Loading…</Card>
          ) : members.length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground">No members found.</Card>
          ) : members.map((m) => {
            const st = STATUS_META[m.is_deceased ? "deceased" : m.application_status] || STATUS_META.incomplete;
            const facilityName = m.facility_name || (m.facility_id ? orgName("facilities", m.facility_id) : "—");
            return (
              <Card key={m.id} data-testid={`member-card-${m.staff_id || m.id}`} className="p-4 cursor-pointer" onClick={() => setSelected(m)}>
                <div className="flex items-start gap-3">
                  {canArchive && (
                    <div onClick={(e) => e.stopPropagation()} className="pt-1">
                      <Checkbox checked={!!checked[m.id]} onCheckedChange={() => toggleOne(m)}
                        data-testid={`member-card-checkbox-${m.staff_id || m.id}`} aria-label={`Select ${m.staff_id || m.id}`} />
                    </div>
                  )}
                  <MemberAvatar member={m} size={42} testIdPrefix="member-avatar-mobile" />
                  <div className="min-w-0 flex-1">
                    <p data-testid={`member-mobile-name-${m.id}`} className="font-medium truncate">{m.full_name || [m.title, m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ")}<RoleBadge role={m.role} testId={`member-mobile-role-${m.id}`} /></p>
                    {!limitedView && <p className="text-xs text-muted-foreground font-mono">{[m.staff_id, m.membership_id].filter(Boolean).join(" · ") || "—"}</p>}
                    <p className="text-xs text-muted-foreground truncate mt-0.5" data-testid={`member-mobile-facility-${m.id}`}>{facilityName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid={`member-mobile-grade-${m.id}`}>{m.professional_grade || "—"}</p>
                    <div className="flex items-center gap-1.5 flex-wrap mt-2">
                      <span data-testid={`member-mobile-status-${m.id}`} className={`text-xs font-medium px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                      {m.overdue && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">{m.days_waiting}d</span>}
                      {m.retiring && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Retired</span>}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelected(m); }}
                    data-testid={`view-member-mobile-${m.staff_id || m.id}`}>View</Button>
                </div>
              </Card>
            );
          })}
        </div>

        <Card className="mt-4 border-border overflow-hidden hidden md:block">
          <div className="overflow-auto max-h-[calc(100vh-340px)]">
            <Table data-testid="members-table">
              <TableHeader className="sticky top-0 z-10 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
                <TableRow className="hover:bg-transparent">
                  {canArchive && (
                    <TableHead className="w-10">
                      <Checkbox checked={allVisibleChecked} onCheckedChange={toggleAll} data-testid="select-all-checkbox" aria-label="Select all" />
                    </TableHead>
                  )}
                  {!limitedView && <SortHead label="Staff ID" col="staff_id" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />}
                  <SortHead label="Full Name" col="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <SortHead label="Name of Facility" col="facility" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="hidden md:table-cell" />
                  <TableHead className={limitedView ? "" : "hidden lg:table-cell"}>Professional Grade</TableHead>
                  <SortHead label="Type of Membership" col="member_type" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="hidden md:table-cell" />
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={canArchive ? 8 : limitedView ? 6 : 7} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
                ) : members.length === 0 ? (
                  <TableRow><TableCell colSpan={canArchive ? 8 : limitedView ? 6 : 7} className="text-center py-10 text-muted-foreground">No members found.</TableCell></TableRow>
                ) : members.map((m) => {
                  const st = STATUS_META[m.is_deceased ? "deceased" : m.application_status] || STATUS_META.incomplete;
                  const facilityName = m.facility_name || (m.facility_id ? orgName("facilities", m.facility_id) : "—");
                  return (
                    <TableRow key={m.id} data-testid={`member-row-${m.staff_id || m.id}`} className="cursor-pointer" onClick={() => setSelected(m)}>
                      {canArchive && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={!!checked[m.id]} onCheckedChange={() => toggleOne(m)}
                            data-testid={`member-checkbox-${m.staff_id || m.id}`} aria-label={`Select ${m.staff_id || m.id}`} />
                        </TableCell>
                      )}
                      {!limitedView && <TableCell className="font-mono text-sm">{m.staff_id || "—"}</TableCell>}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <MemberAvatar member={m} size={34} />
                          <div>
                            <p data-testid={`member-name-${m.id}`} className="font-medium">{m.full_name || [m.title, m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ")}<RoleBadge role={m.role} testId={`member-role-${m.id}`} /></p>
                            {!limitedView && m.membership_id && <p className="text-xs text-muted-foreground font-mono">{m.membership_id}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell data-testid={`member-facility-${m.id}`} className="hidden md:table-cell text-sm text-muted-foreground">{facilityName}</TableCell>
                      <TableCell data-testid={`member-grade-${m.id}`} className={`${limitedView ? "" : "hidden lg:table-cell"} text-sm text-muted-foreground`}>{m.professional_grade || "—"}</TableCell>
                      <TableCell data-testid={`member-type-${m.id}`} className="hidden md:table-cell text-sm text-muted-foreground">{memberTypeLabel(m.member_type)}</TableCell>
                      <TableCell>
                        <span data-testid={`member-status-${m.id}`} className={`text-xs font-medium px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                        {m.overdue && (
                          <span className="ml-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">{m.days_waiting}d</span>
                        )}
                        {m.retiring && (
                          <span className="ml-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Retired</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelected(m); }}
                          data-testid={`view-member-${m.staff_id || m.id}`}>View</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Pagination */}
        {total > 0 && (
          <div className="mt-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted-foreground" data-testid="pagination-summary">
                Showing {((page - 1) * pageSize + 1).toLocaleString()} to {Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()} Members
              </p>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="w-[110px] h-8" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[30, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-1" data-testid="pagination-page-of">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)}
                data-testid="pagination-first" className="gap-1"><CaretDoubleLeft size={14} weight="bold" /> First</Button>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="pagination-prev" className="gap-1"><CaretLeft size={14} weight="bold" /> Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="pagination-next" className="gap-1">Next <CaretRight size={14} weight="bold" /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}
                data-testid="pagination-last" className="gap-1">Last <CaretDoubleRight size={14} weight="bold" /></Button>
              <div className="flex items-center gap-1.5 ml-1">
                <Input type="number" min={1} max={totalPages} value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && jumpToPage()}
                  placeholder="Go to" className="w-20 h-8" data-testid="page-jump-input" />
                <Button variant="outline" size="sm" onClick={jumpToPage} data-testid="page-jump-button">Go</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Member detail dialog */}
      {selected && (
        <MemberDetailsDialog
          member={selected}
          loading={detailLoading}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
          employment={{
            facility: selected.facility_name || facility(selected.facility_id).name,
            facility_type: selected.facility_type || facility(selected.facility_id).type,
            district: selected.district || facility(selected.facility_id).district,
            region: selected.region_name || orgName("regions", selected.region_id),
            chapter: selected.chapter_id ? orgName("chapters", selected.chapter_id) : "",
          }}
          statusSlot={
            <div className="flex items-center gap-3 rounded-md bg-muted px-4 py-3 mt-2 flex-wrap">
              <div className="mr-auto">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Membership ID</p>
                <p className="font-mono text-lg font-semibold tracking-wider">{selected.membership_id || "Not assigned"}</p>
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${(STATUS_META[selected.is_deceased ? "deceased" : selected.application_status] || STATUS_META.incomplete).cls}`}>
                {(STATUS_META[selected.is_deceased ? "deceased" : selected.application_status] || STATUS_META.incomplete).label}
              </span>
              {selected.retiring && (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary" data-testid="retiring-badge">Retiring (age {selected.age})</span>
              )}
            </div>
          }
          footerSlot={
            <>
              {selected.application_status === "pending_facility" && user.role === "facility_manager" && selected.member_type !== "intern" && (
                <>
                  <Button onClick={() => act("approve-facility", "Approved at facility level")} disabled={busy}
                    data-testid="approve-facility-button" className="gap-2">
                    <CheckCircle size={18} weight="bold" /> Facility Approve
                  </Button>
                  <Button variant="outline" disabled={busy} onClick={() => setRejectOpen(true)}
                    data-testid="facility-reject-button" className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground">
                    Reject
                  </Button>
                </>
              )}
              {selected.application_status === "pending_regional" && canApproveRegional && (
                <Button onClick={() => act("approve-regional", "Approved at regional level")} disabled={busy}
                  data-testid="approve-regional-button" className="gap-2">
                  <CheckCircle size={18} weight="bold" /> Regional Approve
                </Button>
              )}
              {selected.application_status === "pending_national" && canApproveNational && (
                <Button onClick={() => act("approve-national", "Approved — Membership ID assigned")} disabled={busy}
                  data-testid="approve-national-button" className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle size={18} weight="bold" /> National Approve & Assign ID
                </Button>
              )}
              {(["pending_regional", "pending_national"].includes(selected.application_status) || (selected.application_status === "pending_facility" && selected.self_registered)) && canReject && (
                <Button variant="outline" disabled={busy} onClick={() => setRejectOpen(true)}
                  data-testid="reject-button" className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground">
                  Reject
                </Button>
              )}
              {canApproveNational && selected.id !== user.id && (
                <Button variant="outline" disabled={busy} onClick={() => navigate(`/members/${selected.id}/edit`)}
                  data-testid="edit-member-button" className="gap-2">
                  <PencilSimple size={18} weight="bold" /> Edit member
                </Button>
              )}
              {canApproveNational && (
                <Button variant="outline" onClick={() => window.open(`/members/${selected.id}/print`, "_blank")}
                  data-testid="print-member-button" className="gap-2">
                  <Printer size={18} weight="bold" /> Print
                </Button>
              )}
              {selected.application_status === "approved" && canApproveNational && (
                <Button variant="outline" disabled={busy}
                  onClick={() => { if (window.confirm("Revert this member's approval? They return to the national approval queue (their Membership ID is kept).")) act("unapprove", "Approval reverted — back in the national queue"); }}
                  data-testid="unapprove-button"
                  className="gap-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white">
                  <ArrowClockwise size={18} weight="bold" /> Unapprove
                </Button>
              )}
              {canResetPw && (
                <Button variant="outline" disabled={busy} onClick={resetPw}
                  data-testid="reset-password-button" className="gap-2">
                  <Key size={18} weight="bold" /> Reset Password
                </Button>
              )}
            </>
          }
        />
      )}

      <RejectDialog open={rejectOpen} onOpenChange={setRejectOpen}
        name={selected ? `${selected.first_name} ${selected.last_name}` : ""}
        onConfirm={(reason) => act("reject", "Application rejected", { reason })} />

      <Dialog open={archiveOpen} onOpenChange={(o) => !o && setArchiveOpen(false)}>
        <DialogContent className="max-w-md" data-testid="archive-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Archive members</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to archive <span className="font-semibold text-foreground">{checkedIds.length}</span> selected member{checkedIds.length !== 1 ? "s" : ""}? They will be moved to the archive and removed from the active directory.
          </p>
          <div className="space-y-1.5 pt-1">
            <Label htmlFor="archive-reason">Reason for archiving <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <div className="flex flex-wrap gap-1.5">
              {ARCHIVE_REASONS.map((r) => (
                <button key={r} type="button" data-testid={`archive-preset-${r}`}
                  onClick={() => setArchiveReason(r)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${archiveReason === r ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                  {r}
                </button>
              ))}
            </div>
            <Textarea id="archive-reason" data-testid="archive-reason-input" rows={3}
              value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="e.g. Resigned, deceased, duplicate record, transferred out of union…" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setArchiveOpen(false)} disabled={archiving}>Cancel</Button>
            <Button variant="destructive" onClick={archiveSelected} disabled={archiving}
              data-testid="confirm-archive-button" className="gap-2">
              <Archive size={16} weight="bold" /> {archiving ? "Archiving…" : "Archive"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={duesOpen} onOpenChange={setDuesOpen}>
        <DialogContent className="max-w-xl" data-testid="dues-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Dues reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Sent automatically <strong>every quarter</strong> to members who pay dues themselves (Self Payment).
              CAGD payroll-deduction members are not reminded. Edit the "How to pay" message below.
            </p>
            <div className="space-y-1.5">
              <Label>Message / how to pay</Label>
              <Textarea rows={9} value={duesText} onChange={(e) => setDuesText(e.target.value)} data-testid="dues-message-body" className="text-sm" />
            </div>
            <div className="flex justify-end">
              <Button onClick={saveDues} disabled={savingDues} data-testid="dues-save-button">{savingDues ? "Saving…" : "Save message"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="staff-id-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Staff ID access log</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Every time a Staff ID is viewed or exported by a National Manager / Super Administrator, it is recorded here for accountability.</p>
          <div className="mt-3 space-y-2">
            {logItems.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="staff-id-log-empty">No access recorded yet.</p>}
            {logItems.map((e, i) => (
              <div key={i} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3 text-sm" data-testid={`staff-id-log-row-${i}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{e.actor_name} <span className="text-xs text-muted-foreground">({e.actor_role})</span></p>
                  <p className="text-xs text-muted-foreground">
                    <span className={`font-medium ${e.action === "export" ? "text-amber-600" : "text-primary"}`}>{e.action === "export" ? "Exported" : "Viewed"}</span>
                    {" · "}{e.target}{e.count ? ` · ${e.count} record${e.count > 1 ? "s" : ""}` : ""}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{e.at ? new Date(e.at).toLocaleString() : ""}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <ExportColumnsDialog open={pickOpen} onOpenChange={setPickOpen} kind="members" onExport={handleExport} exporting={exporting} />
    </Layout>
  );
}
